import { Controller } from '@hotwired/stimulus';
import flatpickr from 'flatpickr';
import { english } from 'flatpickr/dist/l10n/default.js';

export default class extends Controller {
    static targets = ['input'];

    initialize() {
        super.initialize();

        this.onTypeChange = (event) => this._onTypeChange(event);
    }

    async connect() {
        this.dateFormat = this.inputTarget.dataset.format;
        this.withTime = this.inputTarget.dataset.withTime;
        this.dateMin = this.inputTarget.dataset.min;
        this.dateMax = this.inputTarget.dataset.max;
        this.mode = this.inputTarget.dataset.mode;
        this.timeOnly = this.inputTarget.dataset.timeOnly;
        this.rangeSeparator = this.inputTarget.dataset.rangeSeparator;

        if (this.inputTarget.dataset.filterValueInput) {
            this.typeInput = this.inputTarget.closest('form').querySelector(`[data-filter-type-input="${this.inputTarget.dataset.filterValueInput}"]`);

            if (this.typeInput) {
                this.mode = JSON.parse(this.typeInput.selectedOptions[0].dataset.additional).mode;
                this.typeInput.addEventListener('input', this.onTypeChange);
            }
        }

        const language = document.documentElement.lang;
        const languageKey = `./${language}.js`;
        const files = require.context('flatpickr/dist/l10n', false, /\.js$/u, 'lazy');

        if (files.keys().indexOf(languageKey) >= 0) {
            const file = (await files(languageKey)).default;

            if (file[language]) {
                this.locale = file[language];
            } else {
                this.locale = english;
            }
        } else {
            this.locale = english;
        }

        this.initPicker();
    }

    _onTypeChange() {
        this.mode = JSON.parse(this.typeInput.selectedOptions[0].dataset.additional).mode;
        this.initPicker();
    }

    selectWeek(event) {
        const form = this.element.closest('form');

        if (form && event.target.parentElement.classList.contains('flatpickr-weeks')) {
            const indexOfWeekClicked = Array.prototype.indexOf.call(event.target.parentElement.children, event.target);
            const weekdays = Array.from(this.element.querySelector('.flatpickr-rContainer .dayContainer').children);
            const weekdaysGroupedByWeekIndex = [];

            for (let i = 0; i < weekdays.length; i += 7) {
                weekdaysGroupedByWeekIndex.push(weekdays.slice(i, i + 7));
            }

            const selectedWeekDays = weekdaysGroupedByWeekIndex[indexOfWeekClicked] || [];

            if (selectedWeekDays[0]) {
                const start = selectedWeekDays[0].dataset.date;
                const end = selectedWeekDays[selectedWeekDays.length - 1].dataset.date;
                const name = this.inputTarget.name || this.inputTarget.dataset.filterValueInput;

                if (name === 'discount_start_date') {
                    this.picker.setDate(start);
                    const nextElement = form.querySelector('[data-input-key="discount_end_date"]');
                    const nextElementController = Stimulus.getControllerForElementAndIdentifier(nextElement, 'date-picker');
                    nextElementController.picker.setDate(end);
                    this.picker.close();
                }

                if (name === 'discount_end_date') {
                    this.picker.setDate(end);
                    const nextElement = form.querySelector('[data-input-key="discount_start_date"]');
                    const nextElementController = Stimulus.getControllerForElementAndIdentifier(nextElement, 'date-picker');
                    nextElementController.picker.setDate(start);
                    this.picker.close();
                }

                if (name.includes('[discount_start_date]')) {
                    this.picker.setDate(start);
                    const repeaterInputName = name.replace('[discount_start_date]', '[discount_end_date]');
                    const nextElement = form.querySelector(`[name="${repeaterInputName}"]`).closest('.field-holder');
                    const nextElementController = Stimulus.getControllerForElementAndIdentifier(nextElement, 'date-picker');
                    nextElementController.picker.setDate(end);
                    this.picker.close();
                }

                if (name.includes('[discount_end_date]')) {
                    this.picker.setDate(end);
                    const repeaterInputName = name.replace('[discount_end_date]', '[discount_start_date]');
                    const nextElement = form.querySelector(`[name="${repeaterInputName}"]`).closest('.field-holder');
                    const nextElementController = Stimulus.getControllerForElementAndIdentifier(nextElement, 'date-picker');
                    nextElementController.picker.setDate(start);
                    this.picker.close();
                }

                if (name.includes('channels') && name.includes('[start_date]')) {
                    this.picker.setDate(start);
                    const parent = this.inputTarget.closest('.repeater-inputs-holder');
                    const nextElement = parent.querySelector('[data-input-key="end_date"]');
                    const nextElementController = Stimulus.getControllerForElementAndIdentifier(nextElement, 'date-picker');
                    nextElementController.picker.setDate(end);
                    this.picker.close();
                }

                if (name.includes('channels') && name.includes('[end_date]')) {
                    this.picker.setDate(end);
                    const parent = this.inputTarget.closest('.repeater-inputs-holder');
                    const nextElement = parent.querySelector('[data-input-key="start_date"]');
                    const nextElementController = Stimulus.getControllerForElementAndIdentifier(nextElement, 'date-picker');
                    nextElementController.picker.setDate(start);
                    this.picker.close();
                }
            }
        }
    }

    initPicker() {
        if (this.picker) {
            this.picker.destroy();
        }

        const config = {
            ...this.locale,
            firstDayOfWeek: 1,
            rangeSeparator: this.rangeSeparator,
        };

        const options = {
            locale: config,
            disableMobile: true,
            weekNumbers: true,
            dateFormat: this.dateFormat,
            enableTime: this.withTime === '1',
            static: true,
            onDayCreate: (dObj, dStr, fp, dayElem) => {
                dayElem.dataset.date = flatpickr.formatDate(dayElem.dateObj, this.dateFormat);
            }
        };

        if (this.dateMin) {
            options['minDate'] = this.dateMin;
        }

        if (this.dateMax) {
            options['maxDate'] = this.dateMax;
        }

        if (this.mode) {
            options['mode'] = this.mode;
        }

        if (this.timeOnly) {
            options.noCalendar = true;
            options.time_24hr = true;
            options.enableTime = true;
            options.dateFormat = 'H:i';
        }

        this.picker = flatpickr(this.inputTarget, options);
        this.inputTarget.setAttribute('flatpickr-initialized', 'true');
    }

    clear() {
        this.inputTarget.value = '';
    }

    disconnect() {
        if (this.typeInput) {
            this.typeInput.removeEventListener('input', this.onTypeChange);
        }

        this.picker.destroy();
        super.disconnect();
    }
}
